import {
  forwardRef, useEffect, useId, useImperativeHandle, useRef,
  type InputHTMLAttributes, type MouseEvent, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import type { DisabledReasonProps } from './Button.js';
import { useInoperative } from './inoperative.js';

// Forms and inputs (03-components §3.1): label above, control, hint or error below, 16px type on
// coarse pointers (base.css), aria-invalid + aria-describedby on error. Placeholders show an
// example, never the label.

interface FieldChrome {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Marks the field as required; the asterisk is decorative and the attribute carries meaning. */
  required?: boolean;
  /** Visually hides the label where the layout already names the control (a search bar); still announced. */
  hideLabel?: boolean;
  className?: string;
}

function useDescribedBy(id: string, hint: ReactNode, error: ReactNode): { hintId: string; errorId: string; describedBy: string | undefined } {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const ids = [error ? errorId : null, hint ? hintId : null].filter(Boolean);
  return { hintId, errorId, describedBy: ids.length > 0 ? ids.join(' ') : undefined };
}

function FieldShell({ id, label, hint, error, required, hideLabel, className, children, hintId, errorId }: FieldChrome & { id: string; children: ReactNode; hintId: string; errorId: string }) {
  return (
    <div className={['field', error ? 'has-error' : '', className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={id} className={hideLabel ? 'field__label visually-hidden' : 'field__label'}>
        {label}
        {required ? <span className="field__required" aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint ? <p id={hintId} className="field__hint">{hint}</p> : null}
      {error ? <p id={errorId} className="field__error" role="alert">{error}</p> : null}
    </div>
  );
}

export interface TextFieldProps extends FieldChrome, Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'required'> {
  id?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { id: givenId, label, hint, error, required, hideLabel, className, type = 'text', ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const { hintId, errorId, describedBy } = useDescribedBy(id, hint, error);
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} hideLabel={hideLabel} className={className} hintId={hintId} errorId={errorId}>
      <input
        ref={ref}
        id={id}
        type={type}
        className="control"
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
    </FieldShell>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends FieldChrome, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className' | 'required' | 'children'> {
  id?: string;
  options: readonly SelectOption[];
  /** An explicit empty choice, e.g. "Any status". */
  placeholderOption?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { id: givenId, label, hint, error, required, hideLabel, className, options, placeholderOption, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const { hintId, errorId, describedBy } = useDescribedBy(id, hint, error);
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} hideLabel={hideLabel} className={className} hintId={hintId} errorId={errorId}>
      <span className="control-select">
        <select
          ref={ref}
          id={id}
          className="control"
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        >
          {placeholderOption !== undefined ? <option value="">{placeholderOption}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>
          ))}
        </select>
      </span>
    </FieldShell>
  );
});

export interface TextareaFieldProps extends FieldChrome, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className' | 'required'> {
  id?: string;
  /** Auto-grows to this many rows, then scrolls (03-components §3.1). */
  maxRows?: number;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(function TextareaField(
  { id: givenId, label, hint, error, required, hideLabel, className, maxRows = 8, rows = 3, value, defaultValue, onChange, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const { hintId, errorId, describedBy } = useDescribedBy(id, hint, error);
  const inner = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(ref, () => inner.current as HTMLTextAreaElement);

  // Why: measured, not guessed — line-height varies with the type ramp step the control uses.
  useEffect(() => {
    const element = inner.current;
    if (!element) return;
    element.style.blockSize = 'auto';
    const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight) || 20;
    const padding = Number.parseFloat(getComputedStyle(element).paddingBlockStart) + Number.parseFloat(getComputedStyle(element).paddingBlockEnd);
    const max = lineHeight * maxRows + padding;
    const next = Math.min(element.scrollHeight, max);
    element.style.blockSize = `${next}px`;
    element.style.overflowY = element.scrollHeight > max ? 'auto' : 'hidden';
  }, [value, maxRows]);

  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} hideLabel={hideLabel} className={className} hintId={hintId} errorId={errorId}>
      <textarea
        ref={inner}
        id={id}
        className="control control--textarea"
        rows={rows}
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        {...rest}
      />
    </FieldShell>
  );
});

export interface CheckboxProps extends DisabledReasonProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'type'> {
  id?: string;
  label: ReactNode;
  /** Supporting line under the label. */
  description?: ReactNode;
  error?: ReactNode;
  className?: string;
  /** Renders the mixed state for a select-all control. */
  indeterminate?: boolean;
  /**
   * A write of this box's own in flight — a to-do's tick (04-data §1.10.2 rules 3 and 4; Phase 1c finding D25). The box
   * stays focusable and reads as pending: `aria-busy`, greyed by its `aria-disabled` (components.css), its toggle refused
   * until the write settles. Why not `disabled`: under HTML's focus fixup a focused control that becomes disabled loses
   * focus to the document, so a keyboard user who ticked a to-do was sent to the page's start, and a failure could not
   * hand the box back for a one-keystroke retry. It owes no reason (AB-Q7): it is the loading state.
   */
  busy?: boolean;
}

/**
 * The label is part of the hit target and the row is at least 44px on touch (03-components §3.2). A disabled checkbox
 * given a reason says why as `Button` does (`disabledReason`; application baseline 1.1.0 05-client §5.5; the owner's
 * ruling J-D3 of 2026-10-06): it is NOT natively disabled but `aria-disabled`, still focusable, its toggle suppressed
 * (`useInoperative`), and the reason is the row's `title` — the row, not the box, since the row is what the pointer rests
 * on — its accessible description (a hidden element outside the `<label>`, so it never joins the checkbox's name), and a
 * visible bubble over the row on a resting keyboard focus or any press. Without a reason — a fieldset's save in flight,
 * the loading state (AB-Q7) — the native attribute stays.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { id: givenId, label, description, error, className, indeterminate = false, busy = false, disabled, disabledReason, onChange, onClick, onFocus, onBlur, onKeyDown, onPointerLeave, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const inner = useRef<HTMLInputElement | null>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const inoperative = useInoperative<HTMLInputElement>(disabled && disabledReason ? disabledReason : undefined, { onClick, onFocus, onBlur, onKeyDown, onPointerLeave }, `${id}-reason`);
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  // Why every press is refused here while busy: `preventDefault` keeps the box from toggling (Space included, which the
  // browser turns into a click), and the change React would still report for a controlled box is dropped below.
  const pendingProps = busy ? { 'aria-busy': true, 'aria-disabled': true, onClick: (event: MouseEvent<HTMLInputElement>) => event.preventDefault() } as const : null;
  return (
    <div className={['checkbox', error ? 'has-error' : '', className ?? ''].filter(Boolean).join(' ')}>
      <label className="checkbox__row" htmlFor={id} title={inoperative.active ? disabledReason : undefined}>
        <input
          ref={inner}
          id={id}
          type="checkbox"
          className="checkbox__input"
          disabled={!busy && disabled === true && !inoperative.active}
          aria-describedby={[descriptionId, inoperative.reasonId, errorId].filter(Boolean).join(' ') || undefined}
          aria-invalid={error ? true : undefined}
          // Why a change is dropped while inoperative: the click that would toggle it is refused (useInoperative), and
          // React still reports that click as a change of a controlled box.
          onChange={inoperative.active || busy ? undefined : onChange}
          readOnly={inoperative.active || busy || undefined}
          {...rest}
          {...inoperative.controlProps}
          {...pendingProps}
        />
        <span className="checkbox__text">
          <span className="checkbox__label">{label}</span>
          {description ? <span id={descriptionId} className="checkbox__description">{description}</span> : null}
        </span>
      </label>
      {inoperative.reasonElements}
      {error ? <p id={errorId} className="field__error" role="alert">{error}</p> : null}
    </div>
  );
});

export interface FieldsetProps {
  legend: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Groups fields with a legend (03-components §3.3); radio groups and checkbox sets live inside one. */
export function Fieldset({ legend, description, children, className }: FieldsetProps) {
  return (
    <fieldset className={['fieldset', className].filter(Boolean).join(' ')}>
      <legend className="fieldset__legend">{legend}</legend>
      {description ? <p className="fieldset__description">{description}</p> : null}
      <div className="fieldset__body stack stack--3">{children}</div>
    </fieldset>
  );
}

export interface FormErrorSummaryProps {
  /** Messages to summarise at the top of the form, announced in a live region (§3.3). */
  errors: readonly string[];
  title?: string;
}

export function FormErrorSummary({ errors, title = 'Check the form' }: FormErrorSummaryProps) {
  if (errors.length === 0) return null;
  return (
    <div className="form-error-summary" role="alert" tabIndex={-1}>
      <p className="form-error-summary__title">{title}</p>
      <ul>
        {errors.map((message) => <li key={message}>{message}</li>)}
      </ul>
    </div>
  );
}
